import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TagService as CommonTagService } from '@6grain/masofa-monolith-api-client/common/api/tag.service';
import { TagRelationService } from '@6grain/masofa-monolith-api-client/common/api/tagRelation.service';
import { SatelliteProductService } from '@6grain/masofa-monolith-api-client/common/api/satelliteProduct.service';
import { Tag as CommonTag } from '@6grain/masofa-monolith-api-client/common/model/tag';
import { TagDto } from '@6grain/masofa-monolith-api-client/common/model/tagDto';
import { TagService as DictionariesTagService } from '@6grain/masofa-monolith-api-client/dictionaries/api/tag.service';
import { Tag as DictionariesTag } from '@6grain/masofa-monolith-api-client/dictionaries/model/tag';
import { visibleTagFilter } from '@app/utils/tag-filters';

export interface TagDictionaryItem {
  id: string;
  name: string;
  description?: string;
  visible?: boolean;
  isPublic?: boolean;
  comment?: string | null;
  relationCount?: number;
}

export interface TagRelation {
  id: string;
  tagId: string;
  ownerId: string;
  ownerTypeFullName: string;
  tag?: TagDictionaryItem;
}

export interface CreateTagRequest {
  name: string;
  description?: string;
  visible?: boolean;
  isPublic?: boolean;
  comment?: string;
}

export interface CreateTagRelationRequest {
  tagId: string;
  ownerId: string;
  ownerTypeFullName: string;
}

export interface UpdateTagRequest extends CreateTagRequest {
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class TagDictionaryService {
  constructor(
    private readonly dictionariesTagService: DictionariesTagService,
    private readonly commonTagService: CommonTagService,
    private readonly tagRelationService: TagRelationService,
    private readonly satelliteProductService: SatelliteProductService,
  ) {}

  getAllTags(): Observable<TagDictionaryItem[]> {
    return this.dictionariesTagService.dictionariesTagGetAllGet().pipe(
      map((items) => (items ?? []).map((item) => this.toDictionaryItemFromDto(item))),
      catchError(() => of([])),
    );
  }

  getVisibleTags(): Observable<TagDictionaryItem[]> {
    return this.dictionariesTagService.dictionariesTagGetByQueryPost({ filters: [visibleTagFilter] }).pipe(
      map((items) => (items ?? []).map((item) => this.toDictionaryItemFromTag(item))),
      catchError(() => of([])),
    );
  }

  getTagsByOwner(ownerId: string, ownerTypeFullName: string): Observable<TagRelation[]> {
    return this.tagRelationService.commonTagRelationGetByOwnerGet(ownerId, ownerTypeFullName).pipe(
      map((tags) =>
        (tags ?? []).map(
          (tag) =>
            ({
              id: '',
              tagId: tag.id!,
              ownerId,
              ownerTypeFullName,
              tag: this.toDictionaryItemFromDto(tag),
            }) as TagRelation,
        ),
      ),
    );
  }

  /** Returns only the created tag id (Guid). */
  createTag(request: CreateTagRequest): Observable<string> {
    return this.commonTagService.commonTagCreatePost(this.toCommonTag(request));
  }

  createTagDetailed(request: CreateTagRequest): Observable<TagDictionaryItem> {
    return this.dictionariesTagService.dictionariesTagCreatePost(this.toDictionariesTag(request)).pipe(
      map((id) => ({
        id,
        name: request.name,
        description: request.description,
        visible: request.visible ?? true,
        isPublic: request.isPublic ?? false,
        comment: request.comment,
      })),
    );
  }

  updateTag(request: UpdateTagRequest): Observable<TagDictionaryItem> {
    return this.dictionariesTagService.dictionariesTagUpdatePut(this.toDictionariesTag(request, request.id)).pipe(
      map((tag) => this.toDictionaryItemFromTag(tag)),
    );
  }

  createTagRelation(request: CreateTagRelationRequest): Observable<TagRelation> {
    return this.tagRelationService
      .commonTagRelationCreatePost({
        tagId: request.tagId,
        ownerId: request.ownerId,
        ownerTypeFullName: request.ownerTypeFullName,
      })
      .pipe(
        map(
          (relationId) =>
            ({
              id: relationId,
              tagId: request.tagId,
              ownerId: request.ownerId,
              ownerTypeFullName: request.ownerTypeFullName,
            }) as TagRelation,
        ),
      );
  }

  deleteTagRelation(tagRelationId: string): Observable<void> {
    return this.tagRelationService.commonTagRelationDeleteIdDelete(tagRelationId).pipe(map(() => void 0));
  }

  deleteTag(tagId: string): Observable<void> {
    return this.commonTagService.commonTagDeleteIdDelete(tagId).pipe(map(() => void 0));
  }

  /** Adds a tag to a satellite product (creates the tag by name when needed). */
  addTagToSatelliteProduct(productId: string, tagName: string): Observable<boolean> {
    return this.satelliteProductService.commonSatelliteProductAddTagProductIdPost(productId, JSON.stringify(tagName));
  }

  removeTagFromSatelliteProduct(productId: string, tagId: string): Observable<boolean> {
    return this.satelliteProductService.commonSatelliteProductRemoveTagProductIdTagIdDelete(productId, tagId);
  }

  private toDictionaryItemFromDto(dto: TagDto): TagDictionaryItem {
    return {
      id: dto.id!,
      name: dto.name ?? '',
      description: dto.description ?? undefined,
    };
  }

  private toDictionaryItemFromTag(tag: DictionariesTag): TagDictionaryItem {
    return {
      id: tag.id!,
      name: tag.name ?? '',
      description: tag.description ?? undefined,
      visible: tag.visible,
      isPublic: tag.isPublic,
      comment: tag.comment,
      relationCount: tag.relationCount,
    };
  }

  private toCommonTag(request: CreateTagRequest): CommonTag {
    return {
      name: request.name,
      description: request.description,
      visible: request.visible,
      isPublic: request.isPublic,
      comment: request.comment,
    };
  }

  private toDictionariesTag(request: CreateTagRequest, id?: string): DictionariesTag {
    return {
      ...(id ? { id } : {}),
      name: request.name,
      description: request.description,
      visible: request.visible,
      isPublic: request.isPublic,
      comment: request.comment,
    };
  }
}
